import { CancelSaveButtons } from '@components/cancel-save-buttons';
import { createForm, useCreateFormStore } from '@components/form';
import { hideModal, Modal, useModal } from '@components/modal';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';

import { useDuplicateAIAgent } from 'api/ai-agents/hooks';
import { AIAgent, DuplicateAIAgentData } from 'api/ai-agents/requests';

export const useDuplicateAIAgentTemplateModal = () => useModal(DuplicateAIAgentTemplateModal);

const Form = createForm<DuplicateAIAgentData>();

export const DuplicateAIAgentTemplateModal = ({ aiAgent }: { aiAgent: AIAgent }) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();

  const duplicateMutation = useDuplicateAIAgent(aiAgent.shortId);

  const handleSubmit = async () => {
    await duplicateMutation.mutateAsync(formStore.getState().values as DuplicateAIAgentData);
    hideModal();
  };

  const hasFiles = aiAgent.files.length > 0;

  return (
    <Modal>
      <Modal.SimpleHeader title={t('title')} />
      <Modal.Content>
        <Form store={formStore} onSubmit={handleSubmit}>
          <Form.TextField
            id="name"
            label={t('agentName')}
            defaultValue={`${aiAgent.name} [${t('copy')}]`}
          />
          <Stack space={1}>
            <Form.Checkbox
              id="includeFiles"
              label={t('includeFiles')}
              defaultValue={hasFiles}
              isDisabled={!hasFiles}
              tooltip={!hasFiles ? t('noFilesDisabled') : undefined}
            />
          </Stack>
        </Form>
      </Modal.Content>
      <Modal.Footer>
        <CancelSaveButtons
          alwaysShow
          onCancel={hideModal}
          onSave={handleSubmit}
          translationsOverride={{ save: t('confirm') }}
          isDisabled={duplicateMutation.isLoading}
        />
      </Modal.Footer>
    </Modal>
  );
};

export const locale = {
  en: {
    title: 'Duplicate agent',
    agentName: 'Agent name',
    includeFiles: 'Include knowledge files',
    copy: 'copy',
    confirm: 'Duplicate',
    noFilesDisabled: "The agent you're copying does not contain any knowledge files",
  },
};
